import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import clientImg1 from '../assets/kitchen-client-1.jpg';
import clientImg2 from '../assets/kitchen-client-2.jpg';
import clientImg3 from '../assets/kitchen-client-3.jpg';

const Kitchens = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Kitchen Cabinetry | Luxe Cabinetry";
  }, []);

  const galleryImages = [
    clientImg1,
    clientImg2,
    clientImg3
  ];

  const features = [
    'Custom layouts',
    'Island design',
    'Premium finishes',
    'Smart storage systems',
    'Integrated lighting'
  ];

  const processSteps = ['Consultation', 'Design Development', 'Craftsmanship', 'Installation'];

  return (
    <div className="pt-0">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden bg-primary">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.youware.com/users-website-assets/prod/a215f7b1-3c60-4952-a3f0-1a52f1c51f83/8522c22909134f2f8c09341dcc1ccf85.jpg"
            alt="Luxury Kitchen"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary/70 md:bg-primary/80" />
        </div>
        <div className="container-custom relative z-20 text-center text-white px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-2xl md:text-6xl font-bold mb-6 leading-snug md:leading-tight">
              Kitchen Cabinetry Designed <br className="hidden md:block" /> with Precision and Intention
            </h1>
            <p className="text-lg md:text-2xl text-gray-200 font-light mb-10 leading-relaxed">
              Custom-built kitchens that elevate everyday living.
            </p>
            <a
              href="#contact"
              className="bg-accent hover:bg-orange-600 text-white px-8 py-4 uppercase tracking-[0.2em] text-sm font-bold transition-all shadow-lg hover:shadow-orange-500/20 inline-block w-full md:w-auto rounded-lg"
            >
              Schedule Your Design Consultation
            </a>
          </motion.div>
        </div>
      </section>

      {/* Client Image Gallery */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {galleryImages.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative group overflow-hidden shadow-sm ${index === 0 ? 'md:col-span-2 md:h-[600px]' : 'h-[400px]'}`}
              >
                <img
                  src={img}
                  alt={`Custom Kitchen Project ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                />
                {/* No overlay, no buttons */}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Approach */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary">Our Approach to Kitchen Design</h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-light">
            We design cabinetry with architectural balance, spatial efficiency and refined craftsmanship. Every detail is intentional, ensuring your kitchen is not just beautiful, but a joy to use every day.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="order-1 md:order-1">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary">Features</h2>
              <ul className="space-y-6">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center text-lg text-gray-700 font-light border-b border-gray-100 pb-4">
                    <Check size={20} className="text-accent mr-4" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="h-[300px] md:h-[500px] bg-gray-200 relative overflow-hidden shadow-2xl order-2 md:order-2">
              <img 
                src="https://public.youware.com/users-website-assets/prod/a215f7b1-3c60-4952-a3f0-1a52f1c51f83/d21bca857ce34e2789ee64ca1b8e325f.jpg" 
                alt="Kitchen Features" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Snapshot */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-4xl font-bold text-primary/10 mb-4 font-serif">0{index + 1}</div>
                <h3 className="text-lg font-bold text-primary uppercase tracking-wider">{step}</h3>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-6 right-0 w-full h-px bg-primary/10 transform translate-x-1/2 -z-10"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strong CTA */}
      <section className="py-16 md:py-32 bg-neutral text-center">
        <div className="container-custom">
          <h2 className="text-3xl md:text-5xl font-bold mb-8 text-primary">Ready to Transform Your Space?</h2>
          <a
            href="#contact"
            className="bg-accent hover:bg-orange-600 text-white px-10 py-5 uppercase tracking-[0.2em] text-sm font-bold transition-all shadow-lg hover:shadow-orange-500/20 inline-block w-full md:w-auto"
          >
            Schedule Your Design Consultation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Kitchens;
